/**
 * Instant mechanics (no per-tick state): pick, gate, gaze, fixate, heal absorb, shield, knockback.
 */
import type {
  EffectCtx,
  FixateMechanic,
  GateMechanic,
  GazeMechanic,
  HealAbsorbMechanic,
  KnockbackMechanic,
  PickMechanic,
  ShieldMechanic,
  SimContext,
  Unit,
} from '../../contracts';
import { TICK_MS } from '../../contracts';
import {
  amountOf,
  angleTo,
  dist,
  gatePasses,
  hostilesOf,
  immuneToDisplacement,
  pickTargets,
  resolveKeyScaled,
  runEffects,
  unitOf,
  wrapAngle,
} from './state';

/** Default gaze reach (yards). */
export const GAZE_RANGE = 60;
/** Default "looking at it" arc (120°). */
export const GAZE_ARC = (2 * Math.PI) / 3;

export function applyPick(ctx: SimContext, spec: PickMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const n = Math.max(0, Math.round(resolveKeyScaled(ctx, spec.count, 1)));
  const picks = pickTargets(ctx, src, spec.rule, n, ectx.targetId, spec.range);
  for (const u of picks) runEffects(ctx, spec.effects, { ...ectx, targetId: u.id, point: { x: u.pos.x, y: u.pos.y } });
}

export function applyGate(ctx: SimContext, spec: GateMechanic, ectx: EffectCtx): void {
  runEffects(ctx, gatePasses(ctx, spec) ? spec.effects : spec.elseEffects, ectx);
}

/** True when `u` faces `src` within `arc` (total). */
export function isLookingAt(u: Unit, src: Unit, arc = GAZE_ARC): boolean {
  if (u.id === src.id) return false;
  return Math.abs(wrapAngle(angleTo(u.pos, src.pos) - u.facing)) <= arc / 2;
}

export function applyGaze(ctx: SimContext, spec: GazeMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  for (const u of hostilesOf(ctx, src, spec.range ?? GAZE_RANGE)) {
    if (isLookingAt(u, src, spec.arc)) runEffects(ctx, spec.effects, { ...ectx, targetId: u.id, point: { x: u.pos.x, y: u.pos.y } });
  }
}

/** Force `npc` onto `on` until `until` (fixate beats taunt and threat). */
export function setFixate(ctx: SimContext, npc: Unit, on: Unit, until: number): void {
  const t = npc.threat;
  if (!t || !npc.alive) return;
  if (!t.entries[on.id]) t.entries[on.id] = { threat: 0, lastAt: ctx.now };
  t.fixate = { on: on.id, until };
  t.victimId = on.id;
  npc.targetId = on.id;
}

export function applyFixate(ctx: SimContext, spec: FixateMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const until = ctx.now + spec.durationMs;
  let fixaters: Unit[];
  if (spec.adds) {
    fixaters = [];
    for (const id of ctx.world.unitOrder) {
      const u = ctx.world.units[id];
      if (u?.alive && u.summonerId === src.id && u.npcId === spec.adds && u.threat?.fixate == null) fixaters.push(u);
    }
  } else fixaters = src.alive ? [src] : [];
  if (fixaters.length === 0) return;
  const picks = pickTargets(ctx, src, spec.rule ?? 'randomNotTank', fixaters.length, ectx.targetId);
  if (picks.length === 0) return;
  fixaters.forEach((npc, i) => {
    const on = picks[i % picks.length]!;
    setFixate(ctx, npc, on, until);
    if (spec.aura) ctx.sys.combat.applyAura(ctx, on.id, spec.aura.id, npc.id, ectx.abilityId, { durationMs: spec.durationMs });
  });
}

export function applyHealAbsorb(ctx: SimContext, spec: HealAbsorbMechanic, ectx: EffectCtx): void {
  const t = unitOf(ctx, ectx.targetId);
  const src = unitOf(ctx, ectx.sourceId);
  if (!t?.alive) return;
  const prev = t.auras.find((a) => a.auraId === spec.aura.id && (spec.aura.stacking === 'unique' || a.casterId === ectx.sourceId));
  const carried = prev?.absorbRemaining ?? 0;
  const inst = ctx.sys.combat.applyAura(ctx, t.id, spec.aura.id, ectx.sourceId, ectx.abilityId);
  if (!inst) return;
  const amount = Math.round(amountOf(ctx, spec.amount, src, t) * resolveKeyScaled(ctx, spec.scale, 1));
  inst.absorbRemaining = carried + amount;
}

export function applyShield(ctx: SimContext, spec: ShieldMechanic, ectx: EffectCtx): void {
  const holder = unitOf(ctx, spec.on === 'target' ? ectx.targetId : ectx.sourceId);
  if (!holder?.alive) return;
  const pct = resolveKeyScaled(ctx, spec.pctMaxHp, 0);
  const amount = Math.max(1, Math.round(holder.maxHp * pct));
  const inst = ctx.sys.combat.applyAura(ctx, holder.id, spec.aura.id, ectx.sourceId, ectx.abilityId, { absorb: amount });
  // Auras without `absorb` (pure markers) still carry the number for the UI.
  if (inst) inst.absorbRemaining = amount;
}

/** Forced displacement of `t` to `to` over `durationMs` (cancels its cast, like combat's knockback). */
export function displaceUnit(ctx: SimContext, t: Unit, to: { x: number; y: number }, durationMs: number): void {
  const dest = ctx.sys.spatial.clampToWalkable(ctx.world, to);
  const dur = Math.max(TICK_MS, Math.round(durationMs));
  const d = dist(dest, t.pos);
  t.move = { mode: 'forced', to: dest, speed: d / (dur / 1000), until: ctx.now + dur };
  if (t.cast) ctx.sys.casting.cancelCast(ctx, t.id, 'cc');
}

export function applyKnockback(ctx: SimContext, spec: KnockbackMechanic, ectx: EffectCtx): void {
  const t = unitOf(ctx, ectx.targetId);
  const src = unitOf(ctx, ectx.sourceId);
  if (!t?.alive || !src || t.id === src.id || t.ccImmune || immuneToDisplacement(ctx, t)) return;
  const from = spec.from === 'point' && ectx.point ? ectx.point : src.pos;
  let ang = angleTo(from, t.pos);
  if (dist(from, t.pos) < 1e-3) ang = src.facing;
  // A pull (negative distance) never drags the unit past the source.
  const d = spec.distance < 0 ? -Math.min(-spec.distance, Math.max(0, dist(from, t.pos) - 1)) : spec.distance;
  const to = { x: t.pos.x + Math.cos(ang) * d, y: t.pos.y + Math.sin(ang) * d };
  displaceUnit(ctx, t, to, spec.durationMs ?? 400);
}
